import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { mediaUrl } from "@/lib/api";

const CATS = ["all", "cortes", "barbas", "estilo"];

const BeforeAfter = ({ before, after, t }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const move = (clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(97, Math.max(3, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative select-none touch-none cursor-ew-resize"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e.clientX);
      }}
      onPointerMove={(e) => {
        if (e.buttons > 0) move(e.clientX);
      }}
    >
      <img src={after} alt="Después" className="max-h-[78vh] max-w-full object-contain" draggable={false} />
      <img
        src={before}
        alt="Antes"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="h-full w-0.5 -ml-px bg-gold" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-gold text-ink flex items-center justify-center font-display text-xs shadow-lg">
          ↔
        </div>
      </div>
      <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] uppercase bg-ink/85 text-gold px-2.5 py-1 rounded">
        {t("gallery.before")}
      </span>
      <span className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.2em] uppercase bg-ink/85 text-gold px-2.5 py-1 rounded">
        {t("gallery.after")}
      </span>
    </div>
  );
};

export const Gallery = () => {
  const { t } = useI18n();
  const { data } = useSite();
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const items = (data?.gallery || []).filter((g) => cat === "all" || g.category === cat);

  return (
    <section id="galeria" data-testid="gallery-section" className="py-24 sm:py-32 bg-panel/40 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-4">{t("gallery.eyebrow")}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-bone leading-[0.95]">
              {t("gallery.title")}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c}`}
                onClick={() => setCat(c)}
                className={`font-mono text-[11px] uppercase tracking-[0.14em] px-4 py-2 rounded-full border transition-colors ${
                  cat === c
                    ? "bg-gold text-ink border-gold"
                    : "border-bone/20 text-mut hover:border-gold/50 hover:text-gold"
                }`}
              >
                {t(`gallery.${c}`)}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="mt-14 text-mut font-quote italic text-xl" data-testid="gallery-empty-state">
            {t("gallery.empty")}
          </p>
        ) : (
          <div
            data-testid="gallery-images-container"
            className="mt-14 columns-2 md:columns-3 gap-4 [column-fill:_balance]"
          >
            {items.map((g, i) => (
              <motion.figure
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ delay: (i % 3) * 0.07, duration: 0.5 }}
                className="group relative overflow-hidden cursor-pointer mb-4 break-inside-avoid"
                onClick={() => setLightbox(g)}
                data-testid={`gallery-item-${g.id}`}
              >
                <img
                  src={mediaUrl(g.image_path)}
                  alt="Blackbeard Barbería — trabajo"
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {g.before_path && (
                  <span
                    className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] uppercase bg-ink/85 text-gold px-2.5 py-1 rounded"
                    data-testid={`gallery-before-badge-${g.id}`}
                  >
                    Antes / Después
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.figure>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            {lightbox.before_path ? (
              <BeforeAfter before={mediaUrl(lightbox.before_path)} after={mediaUrl(lightbox.image_path)} t={t} />
            ) : (
              <img
                src={mediaUrl(lightbox.image_path)}
                alt="Blackbeard Barbería"
                className="max-h-[85vh] max-w-full object-contain border border-gold/20"
              />
            )}
            <button
              data-testid="gallery-lightbox-close-button"
              className="absolute top-5 right-5 text-bone hover:text-gold p-2"
              onClick={() => setLightbox(null)}
            >
              <X size={28} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
